import { useEffect, useRef } from 'react';
import {
    disableBodyScroll,
    clearAllBodyScrollLocks,
    pxToRem,
} from '@archipro-design/aria';
import { useStyles, Button } from '@archipro-design/aria';
import { CloseLine } from '@archipro-design/icons';
import * as S from './FilterContainer.mobile.style';
import type { FilterContainerProps } from '~/modules/search/components/filter-drawer/FilterContainer';
import FilterSortBy from '~/modules/search/components/filter-drawer/FilterSortBy.mobile';
import FilterViewProjectsBy from '~/modules/search/components/filter-drawer/FilterViewProjectsBy.mobile';

const FilterContainerMobile = ({
    primaryButton,
    secondaryButton,
    onClose,
    disabled,
    children,
    onSortChange,
    sortOptions,
    viewProjectsByOptions,
    onViewProjectsByChange,
    category,
}: FilterContainerProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const { css } = useStyles();

    useEffect(() => {
        if (!contentRef?.current) return;
        disableBodyScroll(contentRef.current);

        return () => {
            clearAllBodyScrollLocks();
        };
    }, [contentRef]);

    return (
        <div className="relative flex h-dvh w-dvw flex-col overflow-hidden">
            <div className="flex h-16 items-start justify-between gap-2 px-4.5 pt-7.5">
                <div className="w-23.5">
                    {secondaryButton && (
                        <Button
                            transparent
                            size={14}
                            variables={{
                                buttonPadding: pxToRem(4),
                            }}
                            {...{
                                ...secondaryButton,
                                children: 'Clear all',
                            }}
                            key="secondaryButton"
                            disabled={disabled}
                        />
                    )}
                </div>
                <h3 className="mt-1 grow text-center font-gt-america text-base font-medium uppercase leading-none">
                    {category === 'blogpost' ? 'Sort by' : 'Filters'}
                </h3>
                <div className="w-23.5 text-right">
                    <button
                        aria-label="Close"
                        disabled={disabled}
                        onClick={(e) => onClose?.(e)}
                    >
                        <CloseLine />
                    </button>
                </div>
            </div>

            <div
                className={`${css(S.ContentContainer)} content-container`}
                ref={contentRef}
            >
                <FilterSortBy
                    sortOptions={sortOptions}
                    onSortChange={onSortChange}
                />
                {viewProjectsByOptions.length > 0 && (
                    <FilterViewProjectsBy
                        viewProjectsByOptions={viewProjectsByOptions}
                        onViewProjectsByChange={onViewProjectsByChange}
                    />
                )}
                {children}
            </div>

            {!!(primaryButton || secondaryButton) && (
                <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-4 border-t border-black/10 px-4.5 py-4">
                    {primaryButton && (
                        <Button
                            size={16}
                            fluid
                            variables={{ buttonPadding: 16 }}
                            {...primaryButton}
                            key="primaryButton"
                            disabled={disabled}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default FilterContainerMobile;
